<script>
// Aussteller-Postfach: without companyId it lists one row per company the user
// is an accepted exhibitor member of; with companyId it shows that company's
// visitor request threads (filterable) and marks the company inbox as read.
import DefaultLayout from '@/components/DefaultLayout.vue'

export default {
  name: 'ExhibitorInboxView',
  components: { DefaultLayout },
  props: {
    companyId: {type: String, default: ''},
  },
  data() {
    return {
      companies: [],
      threads: [],
      companyName: '',
      eventShortNames: [],
      filter: 'all',
      page: 0,
      numPages: 1,
    }
  },
  computed: {
    // "ACME GmbH (ZPE26, ZPS26)" -- company name with active event codes.
    companyTitle() {
      return this.withEvents(this.companyName, this.eventShortNames)
    },
  },
  watch: {
    companyId() { this.load() },
    filter() { if (this.companyId) { this.page = 0; this.fetchThreads() } },
  },
  created() {
    this.load()
  },
  methods: {
    load() {
      if (this.companyId) {
        this.fetchThreads()
        this.markViewed()
      } else {
        this.fetchCompanies()
      }
    },
    fetchCompanies() {
      this.$chat.get('exhibitor-inbox/').then(({data}) => {
        this.companies = data
        // The company picker only matters for multi-company crew members --
        // with exactly one company jump straight into its threads (replace,
        // so the back button skips the empty picker).
        if (this.companies.length === 1) {
          this.$router.replace(`/exhibitor-inbox/${this.companies[0].company_id}`)
        }
      })
    },
    fetchThreads() {
      this.$chat.get(`exhibitor-inbox/${this.companyId}/threads/`,
        {params: {filter: this.filter, page: this.page}}).then(({data}) => {
        this.threads = data.results
        this.companyName = data.company_name || ''
        this.eventShortNames = data.event_short_names || []
        this.numPages = data.num_pages
      })
    },
    // Exhibitor.__str__ format: "ZPE26, ZPS26 - ACME GmbH".
    withEvents(name, shortNames) {
      return (shortNames && shortNames.length)
        ? `${shortNames.join(', ')} - ${name}` : name
    },
    // The row is titled with the visitor's name, so a crew reply needs its own
    // author label -- otherwise it reads as if the visitor had written it.
    lastMessageAuthor(thread) {
      if (!thread.last_message) return ''
      if (thread.last_post_by_me) return 'Du'
      if (!thread.last_post_by_visitor) return thread.last_post_author
      return ''
    },
    markViewed() {
      this.$chat.post(`exhibitor-inbox/${this.companyId}/view/`).catch(() => {})
    },
    openThread(thread) {
      // Lazily join the thread (server checks membership eligibility) so the
      // channel is readable, then reuse the contact-channel view: the member
      // sees the visitor as counterpart.
      this.$chat.post(`exhibitor-inbox/thread/${thread.channel_id}/open/`)
        .then(() => this.$router.push(`/community/${thread.visitor_uuid}/${thread.channel_id}`))
        .catch(() => {})
    },
  },
}
</script>

<template>
  <default-layout>
    <nav class="nav navbar fixed-top bg-white px-2">
      <div class="nav-item">
        <button @click.prevent="$router.back()" class="btn btn-outline">
          <i class="fas fa-chevron-left"></i></button>
      </div>
      <div class="navbar-brand text-truncate flex-fill mb-0">
        <b>{{ companyId ? companyTitle : 'Aussteller-Postfach' }}</b>
      </div>
    </nav>

    <div class="container-fluid mt-2 py-5">

      <!-- Overview: one row per company -->
      <template v-if="!companyId">
        <div v-if="companies.length === 0" class="text-muted text-center mt-4">
          Keine Aussteller-Anfragen.
        </div>
        <div class="list-group mt-1">
          <router-link v-for="c in companies" :key="c.company_id"
           :to="`/exhibitor-inbox/${c.company_id}`" class="text-decoration-none">
            <div class="list-group-item list-group-item-action d-flex align-items-center">
              <img v-if="c.logo" :src="c.logo" class="c-logo me-2" />
              <div class="flex-fill">
                <div class="fw-bold">{{ withEvents(c.name, c.event_short_names) }}</div>
              </div>
              <span v-if="c.unread_count" class="badge bg-danger" title="neue Nachrichten">
                <i class="far fa-envelope"></i> {{ c.unread_count }}
              </span>
            </div>
          </router-link>
        </div>
      </template>

      <!-- Threads of one company -->
      <template v-else>
        <ul class="nav nav-pills mb-3 gap-1">
          <li class="nav-item"><a href="#" class="nav-link" :class="{active: filter==='all'}"
            @click.prevent="filter='all'">Alle</a></li>
          <li class="nav-item"><a href="#" class="nav-link" :class="{active: filter==='unassigned'}"
            @click.prevent="filter='unassigned'">Offen</a></li>
          <li class="nav-item"><a href="#" class="nav-link" :class="{active: filter==='mine'}"
            @click.prevent="filter='mine'">Meine</a></li>
        </ul>

        <div v-if="threads.length === 0" class="text-muted text-center mt-4">
          Keine Anfragen in dieser Ansicht.
        </div>
        <div class="list-group mt-1">
          <a v-for="t in threads" :key="t.channel_id" href="#" class="text-decoration-none"
           @click.prevent="openThread(t)">
            <div class="list-group-item list-group-item-action d-flex align-items-center">
              <div class="flex-fill">
                <div class="fw-bold">
                  {{ t.visitor_name }}
                  <span v-if="t.source_event" class="badge bg-light text-dark border ms-1"
                   title="Messe der ersten Anfrage">{{ t.source_event }}</span>
                  <span v-if="t.assigned_to_me" class="badge bg-info text-dark ms-1">Meine</span>
                  <span v-else-if="t.assigned" class="badge bg-secondary ms-1">Zugewiesen</span>
                </div>
                <small class="text-muted text-truncate d-block">
                  <span v-if="lastMessageAuthor(t)" class="fw-semibold">{{ lastMessageAuthor(t) }}:</span>
                  {{ t.last_message }}
                </small>
              </div>
              <span v-if="t.unread_count" class="badge bg-danger rounded-pill ms-2">{{ t.unread_count }}</span>
            </div>
          </a>
        </div>
      </template>

    </div>
  </default-layout>
</template>

<style scoped>
.c-logo {
  height: 36px;
  width: 36px;
  object-fit: contain;
}
</style>
